import { Menu, Search, Clock, Quote, ThumbsUp, Facebook, Twitter, Instagram, ArrowRight } from 'lucide-react';
import Footer from '../components/Footer';
import { usePageView } from '../hooks/usePageView';
import { useScrollTracking } from '../hooks/useScrollTracking';
import { pushToDataLayer } from '../utils/gtm';

export default function Artikel() {
  usePageView('page_view_report');
  useScrollTracking({ event50: 'scroll_50_report', event90: 'scroll_90_report' });

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Top Bar */}
      <div className="bg-gray-900 text-white text-xs py-2 px-4 hidden md:block">
        <div className="max-w-6xl mx-auto flex justify-between items-center">
          <div className="flex space-x-4">
            <span>Trending:</span>
            <a href="#" className="hover:underline text-gray-300">Wechseljahre</a>
            <a href="#" className="hover:underline text-gray-300">Ernährung</a>
            <a href="#" className="hover:underline text-gray-300">Gesundheit</a>
          </div>
          <div className="flex space-x-4">
            <a href="#" className="hover:underline">Wetter</a>
            <a href="#" className="hover:underline">Börse</a>
            <a href="#" className="hover:underline">Anmelden</a>
          </div>
        </div>
      </div>

      {/* Header / Logo Area */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50 shadow-sm">
        <div className="max-w-6xl mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center space-x-2 md:space-x-4">
            <div className="flex flex-col">
              <span className="text-2xl md:text-4xl font-black tracking-tighter text-red-700 leading-none">GESUNDHEIT</span>
              <span className="text-xs font-bold text-gray-500 uppercase tracking-widest leading-none mt-1">AKTUELL & KOMPAKT</span>
            </div>
          </div>
          <nav className="hidden md:flex space-x-6 text-sm font-bold text-gray-700 uppercase">
            <a href="#" className="text-red-700">Startseite</a>
            <a href="#" className="hover:text-red-700 transition">Abnehmen</a>
            <a href="#" className="hover:text-red-700 transition">Hormone</a>
            <a href="#" className="hover:text-red-700 transition">Ernährung</a>
            <a href="#" className="hover:text-red-700 transition">Psychologie</a>
          </nav>
          <div className="flex items-center space-x-3">
            <span className="hidden md:inline-block text-xs font-semibold bg-blue-100 text-blue-800 px-2 py-1 rounded">ANZEIGE</span>
            <Search className="w-5 h-5 text-gray-600 cursor-pointer" />
          </div>
        </div>
      </header>

      {/* Main Content Grid */}
      <div className="max-w-6xl mx-auto px-4 py-6 md:py-10 grid grid-cols-1 lg:grid-cols-12 gap-10">

        {/* Article Column (Left) */}
        <main className="lg:col-span-8 bg-white p-0 md:p-8 md:rounded-lg md:shadow-sm">

          {/* Breadcrumbs & Category */}
          <div className="flex items-center space-x-2 text-xs text-gray-500 mb-4 px-4 md:px-0 pt-4 md:pt-0">
            <span className="font-bold text-red-700 uppercase">Stoffwechsel</span>
            <span>/</span>
            <span>Frauengesundheit</span>
            <span>/</span>
            <span>Gewichtsmanagement</span>
          </div>

          {/* Headlines */}
          <div className="px-4 md:px-0">
            <h1 className="font-serif text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 leading-tight mb-4">
              Ab 50 tickt der Körper anders: Warum herkömmliche Diäten vor Weihnachten scheitern
            </h1>
            <p className="text-lg md:text-xl text-gray-600 font-serif leading-relaxed mb-6">
              Eine neue 7-Tage-Methode setzt nicht auf Verzicht, sondern auf Verständnis für den weiblichen Stoffwechsel. Experten erklären, was Frauen jetzt tun können, um sich an den Feiertagen endlich wieder wohlzufühlen.
            </p>

            {/* Author & Date */}
            <div className="flex items-center justify-between border-t border-b border-gray-100 py-3 mb-8">
              <div className="flex items-center space-x-3">
                <img src="https://api.dicebear.com/7.x/avataaars/svg?seed=Sarah&gender=female&glasses=round" alt="Autor" className="w-10 h-10 rounded-full bg-gray-100" />
                <div className="flex flex-col text-xs md:text-sm">
                  <span className="font-bold text-gray-900">Von Sarah Meyer</span>
                  <span className="text-gray-500">Gesundheits-Redaktion</span>
                </div>
              </div>
              <div className="text-xs text-gray-400 flex items-center">
                <Clock className="w-3 h-3 mr-1" />
                Lesedauer: 3 Min
              </div>
            </div>
          </div>

          {/* Hero Image */}
          <figure className="mb-8 relative">
            <img src="https://images.unsplash.com/photo-1551024601-569d6aa94578?q=80&w=1000&auto=format&fit=crop" alt="Glückliche Frau über 50 genießt Tee" className="w-full h-auto object-cover rounded-sm" />
            <figcaption className="text-xs text-gray-400 mt-2 px-4 md:px-0">Symbolbild: Endlich wieder wohlfühlen im eigenen Körper – auch im Dezember.</figcaption>
          </figure>

          {/* Article Body */}
          <div className="content-text px-4 md:px-0 font-serif">
            <p className="mb-5 leading-relaxed text-lg text-gray-900">
              <span className="float-left text-5xl font-black text-gray-800 mr-2 mt-[-10px] leading-none">J</span>edes Jahr dasselbe Spiel: Wir freuen uns auf Weihnachten, doch der Blick in den Spiegel und auf die Waage trübt die Vorfreude. Besonders ab der Lebensmitte scheint jedes Plätzchen sofort auf den Hüften zu landen.
            </p>
            <p className="mb-5 leading-relaxed text-lg text-gray-900">
              Der Dezember sollte die Zeit des Genießens sein. Doch für viele Frauen jenseits der 50 schwingt ein ständiger Begleiter mit: <strong>das schlechte Gewissen.</strong>
            </p>
            <p className="mb-5 leading-relaxed text-lg text-gray-900">
              Früher reichte es, vor dem Fest ein paar Tage das Abendessen wegzulassen, und die Hose passte wieder. Heute scheint der Stoffwechsel im Winterschlaf zu sein. Hormonelle Umstellungen in den Wechseljahren sorgen dafür, dass wir sensibler auf Zucker und Stress reagieren.
            </p>

            <div className="bg-red-50 p-4 border-l-4 border-red-500 rounded-r my-6">
              <h3 className="text-xl font-bold text-red-800 mb-2">Das typische Problem ab 50:</h3>
              <p className="text-sm md:text-base text-gray-900 mb-0">Das Ergebnis: Wassereinlagerungen, ein aufgeblähter Bauch und diese hartnäckigen Pölsterchen an der Taille, die sich resistent gegen jede Diät zeigen.</p>
            </div>

            <p className="mb-5 leading-relaxed text-lg text-gray-900">
              Die Frustration ist groß: <em>„Ich esse kaum mehr als früher, aber nehme trotzdem zu."</em>
            </p>
            <p className="mb-5 leading-relaxed text-lg text-gray-900">
              Genau in dieser stressigen Vorweihnachtszeit noch eine strenge Diät zu starten, ist für den Körper ab 50 Gift. Stresshormone (Cortisol) blockieren die Fettverbrennung zusätzlich. Ein Teufelskreis aus Frust, Heißhunger und Erschöpfung beginnt.
            </p>

            <h2 className="text-2xl font-extrabold text-gray-900 mt-10 mb-4">Die Lösung: Kein Kampf gegen den Körper, sondern ein „Reset"</h2>

            <p className="mb-5 leading-relaxed text-lg text-gray-900">
              Statt den Körper durch Verzicht weiter zu stressen, setzen Experten jetzt auf einen völlig anderen Ansatz, der speziell für den weiblichen Organismus in der zweiten Lebenshälfte funktioniert.
            </p>
            <p className="mb-5 leading-relaxed text-lg text-gray-900">
              Es handelt sich um einen <strong>7-Tage-Neustart</strong>, der den Stoffwechsel sanft aufweckt, statt ihn lahmzulegen. Das Ziel ist nicht, in einer Woche 10 Kilo zu verlieren (was unrealistisch und ungesund wäre), sondern Entzündungen zu reduzieren, Wassereinlagerungen loszuwerden und die Hormonbalance zu unterstützen.
            </p>

            {/* Visual Content Breaker */}
            <div className="my-8">
              <div className="bg-gray-100 rounded-lg p-6">
                <h3 className="mt-0 text-center uppercase tracking-wide text-sm text-gray-500 mb-4">Das 4-Säulen-Prinzip</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-white p-4 rounded shadow-sm">
                    <div className="font-bold text-green-600 mb-1">1. Ernährung</div>
                    <div className="text-sm">Stoffwechsel-aktivierende Nährstoffe statt Hungerkuren.</div>
                  </div>
                  <div className="bg-white p-4 rounded shadow-sm">
                    <div className="font-bold text-green-600 mb-1">2. Bewegung</div>
                    <div className="text-sm">Sanfte Übungen, die Gelenke schonen.</div>
                  </div>
                  <div className="bg-white p-4 rounded shadow-sm">
                    <div className="font-bold text-green-600 mb-1">3. Entspannung</div>
                    <div className="text-sm">Cortisol senken für Hormon-Balance.</div>
                  </div>
                  <div className="bg-white p-4 rounded shadow-sm">
                    <div className="font-bold text-green-600 mb-1">4. Schlaf</div>
                    <div className="text-sm">Regeneration optimieren über Nacht.</div>
                  </div>
                </div>
              </div>
            </div>

            <h2 className="text-2xl font-extrabold text-gray-900 mt-10 mb-4">„Endlich spannt die Bluse nicht mehr"</h2>
            <p className="mb-5 leading-relaxed text-lg text-gray-900">
              Teilnehmerinnen der Testgruppe, überwiegend Frauen zwischen 45 und 65, berichten von überraschenden Effekten schon nach wenigen Tagen:
            </p>
            <ul className="list-none pl-0 mb-6">
              <li className="relative pl-6 mb-3 text-lg">
                <span className="absolute left-0 text-green-600 font-bold">✓</span>
                <strong>Der „Blähbauch" verschwindet:</strong> Viele fühlen sich deutlich leichter und die Verdauung beruhigt sich.
              </li>
              <li className="relative pl-6 mb-3 text-lg">
                <span className="absolute left-0 text-green-600 font-bold">✓</span>
                <strong>Energie kehrt zurück:</strong> Statt der typischen Nachmittagsmüdigkeit ist plötzlich wieder Elan für Weihnachtsvorbereitungen da.
              </li>
              <li className="relative pl-6 mb-3 text-lg">
                <span className="absolute left-0 text-green-600 font-bold">✓</span>
                <strong>Die Kleidung sitzt lockerer:</strong> Wassereinlagerungen werden ausgeschwemmt, die Silhouette wirkt gestrafft.
              </li>
            </ul>

            {/* Quote Block */}
            <div className="flex items-start space-x-4 my-8 p-6 bg-white border border-gray-200 rounded-lg shadow-sm italic text-gray-700">
              <Quote className="text-gray-300 w-12 h-12 flex-shrink-0 -mt-2" />
              <div>
                „Ich wollte einfach nur Weihnachten genießen, ohne mich in meinem Festtagskleid wie eine Presswurst zu fühlen. Nach diesen 7 Tagen habe ich nicht nur besser ausgesehen, sondern hatte zum ersten Mal seit Jahren kein schlechtes Gewissen beim Essen."
                <div className="mt-4 not-italic font-bold text-sm text-gray-900">– Sabine, 54 Jahre, Teilnehmerin</div>
              </div>
            </div>

            <h2 className="text-2xl font-extrabold text-gray-900 mt-10 mb-4">Warum jetzt der beste Zeitpunkt ist (nicht erst im Januar)</h2>
            <p className="mb-5 leading-relaxed text-lg text-gray-900">
              Viele denken: <em>„Ach, das mache ich als Vorsatz im neuen Jahr."</em> Doch Experten warnen: Wer jetzt schon gestresst und mit einem belasteten Stoffwechsel in die Völlerei der Feiertage geht, startet im Januar mit einem viel größeren Rucksack – körperlich und mental.
            </p>
            <p className="mb-5 leading-relaxed text-lg text-gray-900">
              Ein kurzer, 7-tägiger Reset <strong>vor</strong> den Feiertagen sorgt dafür, dass:
            </p>
            <ol className="list-decimal pl-6 space-y-2 mb-6 text-gray-800 font-semibold text-lg">
              <li>Der Stoffwechsel aktiv ist, wenn der Gänsebraten kommt.</li>
              <li>Sie sich an Heiligabend in Ihrem Outfit wohl und selbstsicher fühlen.</li>
              <li>Sie die Feiertage entspannt genießen können, weil Sie wissen: „Ich habe die Kontrolle zurück."</li>
            </ol>

            <div className="bg-green-50 border-l-4 border-green-500 p-6 rounded-r my-8">
              <h3 className="mt-0 text-green-800 text-xl font-bold mb-3">Das Angebot: Ein liebevoller „Arschtritt" für den Stoffwechsel</h3>
              <p className="text-sm md:text-base mb-4">
                Für alle Frauen, die keine Lust mehr auf Diät-Frust haben und sich dieses Weihnachten in ihrer Haut wohlfühlen wollen, wurde der <strong>7-Tage-Body-Boost</strong> entwickelt.
              </p>
              <p className="text-sm md:text-base mb-6">
                Es ist eine Anleitung an die Hand, die den mentalen Druck rausnimmt. Einfach umsetzbar, alltagstauglich (auch mit Job und Familie) und speziell abgestimmt auf die Bedürfnisse ab 50.
              </p>

              <div className="text-center">
                <a
                  href="/?headline=Spürbar leichter und Klarer in sieben Tagen"
                  className="bg-red-700 hover:bg-red-800 text-white font-bold py-4 px-6 md:px-10 rounded shadow-lg transform transition hover:scale-105 w-full md:w-auto text-lg uppercase tracking-wide flex items-center justify-center mx-auto"
                  onClick={() => pushToDataLayer('click_to_offer_from_report')}
                >
                  <span>Hier klicken & 7-Tage-Reset starten</span>
                  <ArrowRight className="ml-2 w-5 h-5" />
                </a>
                <p className="text-xs text-gray-500 mt-3">🔒 Limitierter Zugang: Sichern Sie sich jetzt den Vor-Weihnachts-Preis.</p>
              </div>
            </div>

            <p className="text-sm text-gray-600 italic">
              <strong>Garantie:</strong> Wenn Sie sich nach 7 Tagen nicht vitaler fühlen, erhalten Sie Ihr Geld zurück. Wir wissen, dass es funktioniert.
            </p>

          </div>

          {/* Author Bio / Trust Section Bottom */}
          <div className="mt-12 border-t pt-8 px-4 md:px-0 flex flex-col md:flex-row items-center md:items-start text-center md:text-left">
            <div className="w-16 h-16 bg-gray-200 rounded-full flex-shrink-0 mb-4 md:mb-0 md:mr-4 overflow-hidden">
              <img src="https://api.dicebear.com/7.x/avataaars/svg?seed=Sarah&gender=female&glasses=round" alt="Sarah Meyer" />
            </div>
            <div>
              <h4 className="font-bold text-gray-900">Über Sarah Meyer</h4>
              <p className="text-sm text-gray-600 mt-1">Sarah ist spezialisiert auf Frauengesundheit und hormonelle Balance. Sie recherchiert leidenschaftlich Themen, die Frauen ab der Lebensmitte helfen, sich wieder wohlzufühlen.</p>
            </div>
          </div>

          {/* Comments Fake Section */}
          <div className="mt-12 bg-gray-50 p-6 rounded-lg mx-4 md:mx-0">
            <h3 className="font-bold text-lg mb-4 text-gray-800">Leserkommentare (3)</h3>
            <div className="space-y-4">
              <div className="border-b pb-4">
                <div className="flex justify-between items-center mb-1">
                  <span className="font-bold text-sm text-blue-800">Monika S.</span>
                  <span className="text-xs text-gray-500">vor 2 Stunden</span>
                </div>
                <p className="text-sm text-gray-700">Endlich mal ein Artikel, der uns nicht als 20-Jährige behandelt. Das Hormon-Thema wird so oft ignoriert. Danke!</p>
                <div className="flex items-center mt-2 space-x-3">
                  <span className="text-xs text-gray-500 cursor-pointer hover:text-blue-600">Antworten</span>
                  <div className="flex items-center text-xs text-gray-400">
                    <ThumbsUp className="w-3 h-3 mr-1" /> 12
                  </div>
                </div>
              </div>
              <div className="border-b pb-4">
                <div className="flex justify-between items-center mb-1">
                  <span className="font-bold text-sm text-blue-800">Renate65</span>
                  <span className="text-xs text-gray-500">vor 5 Stunden</span>
                </div>
                <p className="text-sm text-gray-700">Habe den Reset letztes Jahr gemacht. Es ist wirklich machbar, kein Hungern. Mein Bauch war an Heiligabend tatsächlich flacher.</p>
                <div className="flex items-center mt-2 space-x-3">
                  <span className="text-xs text-gray-500 cursor-pointer hover:text-blue-600">Antworten</span>
                  <div className="flex items-center text-xs text-gray-400">
                    <ThumbsUp className="w-3 h-3 mr-1" /> 24
                  </div>
                </div>
              </div>
              <div>
                <div className="flex justify-between items-center mb-1">
                  <span className="font-bold text-sm text-blue-800">Anja</span>
                  <span className="text-xs text-gray-500">vor 8 Stunden</span>
                </div>
                <p className="text-sm text-gray-700">Klingt logisch mit dem Cortisol. Ich werde es probieren.</p>
                <div className="flex items-center mt-2 space-x-3">
                  <span className="text-xs text-gray-500 cursor-pointer hover:text-blue-600">Antworten</span>
                  <div className="flex items-center text-xs text-gray-400">
                    <ThumbsUp className="w-3 h-3 mr-1" /> 5
                  </div>
                </div>
              </div>
            </div>
          </div>

        </main>

        {/* Sidebar Column (Right) */}
        <aside className="lg:col-span-4 space-y-8 px-4 md:px-0 hidden lg:block">

          {/* Sticky Widget */}
          <div className="sticky top-8">
            {/* Most Read */}
            <div className="bg-white p-6 shadow-sm rounded-sm border-t-4 border-gray-800 mb-8">
              <h3 className="font-bold text-gray-900 uppercase tracking-widest text-xs mb-4">Meistgelesen</h3>
              <ul className="space-y-4">
                <li className="flex items-start group cursor-pointer">
                  <span className="text-2xl font-black text-gray-200 mr-3 group-hover:text-red-700 transition">1</span>
                  <span className="text-sm font-semibold text-gray-700 leading-snug group-hover:text-red-700 transition">Warum Sie ab 18 Uhr keine Rohkost mehr essen sollten</span>
                </li>
                <li className="flex items-start group cursor-pointer">
                  <span className="text-2xl font-black text-gray-200 mr-3 group-hover:text-red-700 transition">2</span>
                  <span className="text-sm font-semibold text-gray-700 leading-snug group-hover:text-red-700 transition">3 Anzeichen, dass Ihre Schilddrüse Hilfe braucht</span>
                </li>
                <li className="flex items-start group cursor-pointer">
                  <span className="text-2xl font-black text-gray-200 mr-3 group-hover:text-red-700 transition">3</span>
                  <span className="text-sm font-semibold text-gray-700 leading-snug group-hover:text-red-700 transition">Wechseljahre: Dieses Gewürz sollten Sie kennen</span>
                </li>
                <li className="flex items-start group cursor-pointer">
                  <span className="text-2xl font-black text-gray-200 mr-3 group-hover:text-red-700 transition">4</span>
                  <span className="text-sm font-semibold text-gray-700 leading-snug group-hover:text-red-700 transition">Der 7-Tage-Stoffwechsel-Trick (Artikel)</span>
                </li>
              </ul>
            </div>

            {/* Offer Widget */}
            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden shadow-lg">
              <div className="bg-red-700 text-white text-center py-2 font-bold uppercase text-sm">
                Leser-Tipp der Woche
              </div>
              <div className="p-6 text-center">
                <img src="https://placehold.co/400x300/e2e8f0/475569?text=7-Tage+Programm" alt="Programm Cover" className="w-full h-32 object-cover mb-4 rounded" />
                <h4 className="font-bold text-gray-900 mb-2">Weihnachten ohne Reue?</h4>
                <p className="text-sm text-gray-600 mb-4">Starten Sie jetzt den 7-Tage-Reset und fühlen Sie sich leichter.</p>
                <button className="bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-4 rounded w-full text-sm transition">
                  Verfügbarkeit prüfen
                </button>
              </div>
            </div>

            {/* Ad Placeholder */}
            <div className="mt-8 bg-gray-100 h-64 flex items-center justify-center text-gray-400 text-xs border border-gray-200">
              Anzeige
            </div>
          </div>

        </aside>

      </div>

      {/* Mobile Sticky CTA */}
      <div className="fixed bottom-0 left-0 w-full bg-white border-t border-gray-200 p-3 md:hidden z-50 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.1)]">
        <a
          href="/?headline=Spürbar leichter und Klarer in sieben Tagen"
          className="bg-red-700 text-white font-bold w-full py-3 rounded text-sm uppercase block text-center"
          onClick={() => pushToDataLayer('click_to_offer_from_report')}
        >
          Jetzt Stoffwechsel starten &raquo;
        </a>
      </div>

      {/* Footer */}
      <footer className="bg-black text-gray-400 py-12 text-sm mt-12 pb-24 md:pb-12">
        <div className="max-w-6xl mx-auto px-4">
          <div className="mb-8">
            <h5 className="text-white font-bold uppercase mb-4 tracking-wider">Gesundheit Aktuell</h5>
            <p className="text-gray-400">Ihr täglicher Ratgeber für ein gesundes, vitales Leben ab 50.</p>
          </div>

          <div className="border-t border-gray-800 pt-8">
            <h5 className="text-white font-bold uppercase mb-4 tracking-wider">Rechtliches</h5>
            <ul className="flex flex-wrap gap-4 mb-6">
              <li><a href="/impressum" className="hover:text-white transition">Impressum</a></li>
              <li><a href="/datenschutz" className="hover:text-white transition">Datenschutz</a></li>
            </ul>

            <div className="mb-6">
              <h5 className="text-white font-bold uppercase mb-4 tracking-wider">Disclaimer</h5>
              <p className="text-xs leading-relaxed text-gray-400">
                Dieser Artikel dient nur zu Informationszwecken und ersetzt keine ärztliche Beratung. Die dargestellten Personen sind Models. Dies ist eine Werbeanzeige (Advertorial).
              </p>
            </div>

            <p className="text-xs text-gray-500 border-t border-gray-800 pt-4">
              © 2025 Christel Media Marketing GmbH.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
